/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Route} from './config';
import {RouterStateSnapshot} from './router_state';
/**
 * \@whatItDoes Represents an event triggered when a navigation starts.
 * 
 * \@stable
 */
export class NavigationStart {
/**
 * @param {?} id
 * @param {?} url
 */
constructor(
public id: number,
public url: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return `NavigationStart(id: ${this.id}, url: '${this.url}')`; }
}

function NavigationStart_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationStart.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationStart.prototype.url;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation ends successfully.
 * 
 * \@stable
 */
export class NavigationEnd {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `NavigationEnd(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}')`;
  }
}

function NavigationEnd_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.urlAfterRedirects;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation is canceled.
 * 
 * \@stable
 */
export class NavigationCancel {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} reason
 */
constructor(
public id: number,
public url: string,
public reason: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return `NavigationCancel(id: ${this.id}, url: '${this.url}')`; }
}

function NavigationCancel_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.reason;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation fails due to an unexpected error.
 * 
 * \@stable
 */
export class NavigationError {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} error
 */
constructor(
public id: number,
public url: string,
public error: any) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `NavigationError(id: ${this.id}, url: '${this.url}', error: ${this.error})`;
  }
}

function NavigationError_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.error;
}

/**
 * \@whatItDoes Represents an event triggered when routes are recognized.
 * 
 * \@stable
 */
export class RoutesRecognized {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `RoutesRecognized(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state})`;
  }
}

function RoutesRecognized_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.state;
}

/**
 * \@whatItDoes Represents an event triggered before lazy loading a route config.
 * 
 * \@experimental
 */
export class RouteConfigLoadStart {
/**
 * @param {?} route
 */
constructor(public route: Route) {}
/**
 * @return {?}
 */
toString(): string { return `RouteConfigLoadStart(path: ${this.route.path})`; }
}

function RouteConfigLoadStart_tsickle_Closure_declarations() {
/** @type {?} */
RouteConfigLoadStart.prototype.route;
}

/**
 * \@whatItDoes Represents an event triggered when a route has been lazy loaded.
 * 
 * \@experimental
 */
export class RouteConfigLoadEnd {
/**
 * @param {?} route
 */
constructor(public route: Route) {}
/**
 * @return {?}
 */
toString(): string { return `RouteConfigLoadEnd(path: ${this.route.path})`; }
}

function RouteConfigLoadEnd_tsickle_Closure_declarations() {
/** @type {?} */
RouteConfigLoadEnd.prototype.route;
}

/**
 * \@whatItDoes Represents the start of the Guard phase of routing.
 * 
 * \@experimental
 */
export class GuardsCheckStart {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot) {}
/**
 * @return {?}
 */
toString(): string {
    return `GuardsCheckStart(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state})`;
  }
}

function GuardsCheckStart_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckStart.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckStart.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckStart.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckStart.prototype.state;
}

/**
 * \@whatItDoes Represents the end of the Guard phase of routing.
 * 
 * \@experimental
 */
export class GuardsCheckEnd {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 * @param {?} shouldActivate
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot,
public shouldActivate: boolean) {}
/**
 * @return {?}
 */
toString(): string {
    return `GuardsCheckEnd(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state}, shouldActivate: ${this.shouldActivate})`;
  }
}

function GuardsCheckEnd_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckEnd.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckEnd.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckEnd.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckEnd.prototype.state;
/**
 * \@docsNotRequired
 * @type {?}
 */
GuardsCheckEnd.prototype.shouldActivate;
}

/**
 * \@whatItDoes Represents the start of the Resolve phase of routing. The timing of this
 * event may change, thus it's experimental. In the current iteration it will run
 * in the "resolve" phase whether there's things to resolve or not. In the future this
 * behavior may change to only run when there are things to be resolved.
 * 
 * \@experimental
 */
export class ResolveStart {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot) {}
/**
 * @return {?}
 */
toString(): string {
    return `ResolveStart(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state})`;
  }
}

function ResolveStart_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveStart.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveStart.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveStart.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveStart.prototype.state;
}

/**
 * \@whatItDoes Represents the end of the Resolve phase of routing. See note on
 * {\@link ResolveStart} for use of this experimental API.
 * 
 * \@experimental
 */
export class ResolveEnd {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot) {}
/**
 * @return {?}
 */
toString(): string {
    return `ResolveEnd(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state})`;
  }
}

function ResolveEnd_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveEnd.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveEnd.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveEnd.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
ResolveEnd.prototype.state;
}


/**
 * @whatItDoes Represents a router event, allowing you to track the lifecycle of the router.
 *
 * The sequence of router events is:
 *
 * - {@link NavigationStart},
 * - {@link RouteConfigLoadStart},
 * - {@link RouteConfigLoadEnd},
 * - {@link RoutesRecognized},
 * - {@link GuardsCheckStart},
 * - {@link GuardsCheckEnd},
 * - {@link ResolveStart},
 * - {@link ResolveEnd},
 * - {@link NavigationEnd},
 * - {@link NavigationCancel},
 * - {@link NavigationError}
 *
 * @stable
 */
export type Event = NavigationStart | NavigationEnd | NavigationCancel | NavigationError |
    RoutesRecognized | RouteConfigLoadStart | RouteConfigLoadEnd | GuardsCheckStart |
    GuardsCheckEnd | ResolveStart | ResolveEnd;
